import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TopBar } from "../components/TopBar";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import {
  api,
  STATUS_LABELS,
  STATUS_ORDER,
  formatDate,
  formatRelative,
  canEditAll,
  isThirdParty,
  publicOrigin,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCw,
  Save,
  History,
  CircleAlert,
  Truck,
  ClipboardList,
  Building2,
  Link2,
  ExternalLink,
  Package,
  Image as ImageIcon,
  Trash2,
  Flame,
  Printer,
  MessageSquare,
  Eye,
  FileText,
} from "lucide-react";
import { StepBar, buildStagesFromRecord } from "../components/ProgressTimeline";
import { SpecImageUploader } from "../components/SpecImageUploader";
import { DocumentsPanel } from "../components/DocumentsPanel";
import { ShareTrackingDropdown } from "../components/ShareTrackingDropdown";
import { SkuCombobox } from "../components/SkuCombobox";
import { CommentsPanel } from "../components/CommentsPanel";
import { Switch } from "../components/ui/switch";
import { useSettings } from "../context/SettingsContext";

const THIRD_PARTY_ALLOWED = ["collected", "in_specification", "ready_for_return", "returned", "issue"];

export default function BathDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const settings = useSettings();
  const [record, setRecord] = useState(null);
  const [audit, setAudit] = useState([]);
  const [thirdParties, setThirdParties] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  const isTP = isThirdParty(user);
  const isReadOnly = user?.role === "read_only";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a, tp] = await Promise.all([
        api.get(`/bath-records/${id}`),
        api.get(`/bath-records/${id}/audit`),
        api.get("/third-parties"),
      ]);
      setRecord(r.data);
      setForm(r.data);
      setAudit(a.data);
      setThirdParties(tp.data);
    } catch (e) {
      toast.error("Could not load record");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Mark any unread @mentions on this record as read on first view
  useEffect(() => {
    if (!id) return;
    api
      .post(`/users/me/mentions/read?bath_record_id=${encodeURIComponent(id)}`)
      .then(() => {
        // Tell the topbar bell to refresh its count
        window.dispatchEvent(new CustomEvent("flowline:mentions-changed"));
      })
      .catch(() => {});
  }, [id]);

  if (loading || !record) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <TopBar />
        <div className="p-8 text-zinc-400 text-sm">Loading record…</div>
      </div>
    );
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(record);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Compute diff
      const diff = {};
      Object.keys(form).forEach((k) => {
        if (form[k] !== record[k]) diff[k] = form[k];
      });
      if (Object.keys(diff).length === 0) {
        toast.message("No changes to save");
        return;
      }
      const r = await api.patch(`/bath-records/${id}`, diff);
      setRecord(r.data);
      setForm(r.data);
      const a = await api.get(`/bath-records/${id}/audit`);
      setAudit(a.data);
      toast.success("Record updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (newStatus) => {
    setSaving(true);
    try {
      const r = await api.patch(`/bath-records/${id}`, { status: newStatus });
      setRecord(r.data);
      setForm(r.data);
      const a = await api.get(`/bath-records/${id}/audit`);
      setAudit(a.data);
      toast.success(`Status → ${STATUS_LABELS[newStatus]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/bath-records/${id}/sync`);
      setRecord(r.data);
      setForm(r.data);
      const a = await api.get(`/bath-records/${id}/audit`);
      setAudit(a.data);
      toast.success("Refreshed from Brightpearl");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const allowedStatuses = isTP
    ? STATUS_ORDER.filter((s) => THIRD_PARTY_ALLOWED.includes(s) || s === record.status)
    : STATUS_ORDER;

  // Third-party users can VIEW every record (like the old Google Sheet) but
  // can only EDIT records assigned to their own company. Mirror the backend
  // permission on the UI so we don't invite failing PATCHes.
  const tpOwnsRecord = isTP && record?.third_party_id === user?.third_party_id;
  const canEditFields = !isReadOnly && (canEditAll(user) || tpOwnsRecord);
  const tpDisable = (field) => isTP && !["status", "notes", "date_collected", "date_returned", "date_ready_for_return", "expected_return_date", "spec_image_path", "is_urgent", "urgent_reason"].includes(field);

  const stages = buildStagesFromRecord(record);
  const trackingUrl = record.tracking_code
    ? `${publicOrigin()}/track/${record.tracking_code}`
    : null;

  const copyTrackingLink = async () => {
    if (!trackingUrl) return;
    try {
      await navigator.clipboard.writeText(trackingUrl);
      toast.success("Customer tracking link copied");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const refreshAfterImage = async (hasImg) => {
    const r = await api.get(`/bath-records/${id}`);
    setRecord(r.data);
    setForm(r.data);
    const a = await api.get(`/bath-records/${id}/audit`);
    setAudit(a.data);
  };

  const deleteRecord = async () => {
    const ok = window.confirm(
      `Delete this bath record?\n\n${record.supplier_po} · ${record.description}\n\nThis cannot be undone. The customer's tracking link will stop working.`
    );
    if (!ok) return;
    try {
      await api.delete(`/bath-records/${id}`);
      toast.success("Bath record deleted");
      navigate(isTP ? "/third-party" : "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />

      <main className="mx-auto max-w-[1500px] px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <Link to={isTP ? "/third-party" : "/dashboard"} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5" data-testid="back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to {isTP ? "assignments" : "dashboard"}
          </Link>
          <div className="flex items-center gap-2">
            {canEditAll(user) && (
              <Button
                size="sm"
                variant="outline"
                onClick={deleteRecord}
                className="border-zinc-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                data-testid="delete-record-button"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </Button>
            )}
            {canEditAll(user) && record.brightpearl_id && (
              <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="sync-record-button">
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                Sync from Brightpearl
              </Button>
            )}
            {canEditAll(user) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/bath/${id}/label`)}
                className="border-zinc-200"
                data-testid="print-label-button"
                title="Open printable 100×150mm barcode label"
              >
                <Printer className="h-3.5 w-3.5 mr-1.5" />
                Print label
              </Button>
            )}
            {canEditFields && (
              <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="save-record-button">
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {saving ? "Saving…" : "Save changes"}
              </Button>
            )}
          </div>
        </div>

        {/* Third-party view-only banner (record not assigned to their company) */}
        {isTP && !tpOwnsRecord && (
          <div
            className="bg-blue-50 border border-blue-200 text-blue-900 rounded-md px-4 py-2.5 text-xs mb-4 flex items-center gap-2"
            data-testid="tp-view-only-banner"
          >
            <Eye className="h-3.5 w-3.5 shrink-0" />
            <span>
              You&apos;re viewing this record for context — it&apos;s not assigned to your company, so editing is disabled.
            </span>
          </div>
        )}

        {/* Header */}
        <div className="bg-white border border-zinc-200 rounded-md p-5 shadow-sm mb-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 rounded text-zinc-700 font-semibold">
                  {record.brightpearl_id || "—"}
                </span>
                <StatusBadge status={record.status} />
                {record.status === "issue" && <CircleAlert className="h-4 w-4 text-rose-600" />}
                {record.is_urgent && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                    title={record.urgent_reason || "Marked urgent"}
                    data-testid="urgent-badge"
                  >
                    <Flame className="h-3 w-3" /> Urgent
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const el = document.querySelector('[data-testid="comments-section"]');
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border transition-colors ${
                    commentCount > 0
                      ? "bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800"
                      : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"
                  }`}
                  title="Jump to comments"
                  data-testid="header-comments-jump"
                >
                  <MessageSquare className="h-3 w-3" />
                  {commentCount} {commentCount === 1 ? "comment" : "comments"}
                </button>
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tighter text-zinc-950">
                {record.description}
              </h1>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs">
                <Spec label={`${settings.supplier_name} PO`} value={record.supplier_po} mono hint="Plain bath supplier" />
                <Spec label="Giavani PO" value={record.internal_po} mono hint="WeLove's PO to Giavani" />
                {record.third_party_po && (
                  <Spec
                    label={`${record.third_party_name || "Giavani"} ref`}
                    value={record.third_party_po}
                    mono
                    hint="Their paperwork reference"
                  />
                )}
                {record.revised_po && (
                  <Spec label="Revised PO" value={record.revised_po} mono hint="Replaces / amends original" />
                )}
                <Spec label="Sales Order" value={record.sales_order} mono />
                <Spec label="SKU" value={record.product_sku} mono />
                {record.alternate_sku && (
                  <Spec label="Alternate SKU" value={record.alternate_sku} mono hint="Same bath, alternate code" />
                )}
                <Spec label="Barcode" value={record.barcode} mono />
                <Spec label="Tracking code" value={record.tracking_code} mono hint="Customer-facing" />
                {record.customer_required_date && (
                  <Spec label="Customer required by" value={formatDate(record.customer_required_date)} hint="Hard deadline" />
                )}
              </div>
            </div>
            <div className="text-right text-[11px] text-zinc-500">
              <div>Updated {formatRelative(record.updated_at)}</div>
              <div>by <span className="font-semibold text-zinc-700">{record.updated_by}</span></div>
              {record.brightpearl_last_synced && (
                <div className="mt-1">BP sync: {formatRelative(record.brightpearl_last_synced)}</div>
              )}
            </div>
          </div>

          {/* Quick status actions */}
          {canEditFields && (
            <div className="mt-4 pt-4 border-t border-zinc-100 flex flex-wrap gap-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold self-center mr-1">
                Quick status →
              </span>
              {allowedStatuses.filter((s) => s !== record.status).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="outline"
                  onClick={() => quickStatus(s)}
                  disabled={saving}
                  className="h-7 text-xs border-zinc-200"
                  data-testid={`quick-status-${s}`}
                >
                  {STATUS_LABELS[s]}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Customer-facing progress bar */}
        <div className="bg-white border border-zinc-200 rounded-md p-5 sm:p-8 shadow-sm mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="font-heading font-bold text-sm tracking-tight text-zinc-950">
                Customer-facing progress
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                What the customer sees when they enter their tracking code.
              </p>
            </div>
            {trackingUrl && (
              <div className="flex items-center gap-2">
                <ShareTrackingDropdown url={trackingUrl} code={record.tracking_code} product={record.description} />
                <a
                  href={`/track/${record.tracking_code}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-zinc-950 hover:underline flex items-center gap-1"
                  data-testid="open-tracking-page"
                >
                  Open <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>
          <StepBar stages={stages} hasIssue={record.status === "issue"} />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Edit form */}
          <div className="lg:col-span-2 space-y-4">
            <Section title="Order details" icon={ClipboardList}>
              <Grid>
                <Field label={`${settings.supplier_name} PO`} mono>
                  <Input value={form.supplier_po || ""} onChange={(e) => update("supplier_po", e.target.value)} disabled={!canEditFields || tpDisable("supplier_po")} className="font-mono" data-testid="field-supplier-po" />
                </Field>
                <Field label="Giavani PO" mono>
                  <Input value={form.internal_po || ""} onChange={(e) => update("internal_po", e.target.value)} disabled={!canEditFields || tpDisable("internal_po")} className="font-mono" data-testid="field-internal-po" />
                </Field>
                <Field label="Revised / Amended PO" mono>
                  <Input
                    value={form.revised_po || ""}
                    onChange={(e) => update("revised_po", e.target.value)}
                    disabled={!canEditFields || tpDisable("revised_po")}
                    placeholder="Optional — original stays searchable"
                    className="font-mono"
                    data-testid="field-revised-po"
                  />
                </Field>
                <Field label="Sales Order" mono>
                  <Input value={form.sales_order || ""} onChange={(e) => update("sales_order", e.target.value)} disabled={!canEditFields || tpDisable("sales_order")} className="font-mono" data-testid="field-sales-order" />
                </Field>
                <Field label="Customer reference">
                  <Input value={form.customer_ref || ""} onChange={(e) => update("customer_ref", e.target.value)} disabled={!canEditFields || tpDisable("customer_ref")} data-testid="field-customer-ref" />
                </Field>
                <Field label="Product SKU" mono>
                  {canEditFields && !tpDisable("product_sku") ? (
                    <SkuCombobox
                      value={form.product_sku || ""}
                      onChange={(v) => update("product_sku", v)}
                      onPick={(entry) => {
                        setForm((f) => ({
                          ...f,
                          product_sku: entry.sku || f.product_sku,
                          alternate_sku: entry.alternate_sku || f.alternate_sku || "",
                          description: f.description || entry.description || "",
                          barcode: f.barcode || entry.barcode || "",
                        }));
                      }}
                      testId="field-product-sku"
                    />
                  ) : (
                    <Input value={form.product_sku || ""} disabled className="font-mono" data-testid="field-product-sku" />
                  )}
                </Field>
                <Field label="Alternate SKU" mono>
                  <Input
                    value={form.alternate_sku || ""}
                    onChange={(e) => update("alternate_sku", e.target.value)}
                    disabled={!canEditFields || tpDisable("alternate_sku")}
                    placeholder="Optional — alternate code for the same bath"
                    className="font-mono"
                    data-testid="field-alternate-sku"
                  />
                </Field>
                <Field label="Barcode" mono>
                  <Input value={form.barcode || ""} onChange={(e) => update("barcode", e.target.value)} disabled={!canEditFields || tpDisable("barcode")} className="font-mono" data-testid="field-barcode" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Description">
                    <Input value={form.description || ""} onChange={(e) => update("description", e.target.value)} disabled={!canEditFields || tpDisable("description")} data-testid="field-description" />
                  </Field>
                </div>
                {/* Urgent flag */}
                {canEditFields && !tpDisable("is_urgent") && (
                  <div className="md:col-span-2 rounded-md border border-amber-200 bg-amber-50/40 p-3 flex flex-wrap items-center gap-3">
                    <Flame className={`h-4 w-4 ${form.is_urgent ? "text-amber-600" : "text-zinc-400"}`} />
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-semibold text-zinc-900">Mark this order as urgent</div>
                      <div className="text-[11px] text-zinc-500">Highlights the row on the dashboard with an amber accent.</div>
                    </div>
                    <Switch
                      checked={!!form.is_urgent}
                      onCheckedChange={(v) => update("is_urgent", v)}
                      data-testid="field-urgent-toggle"
                    />
                    {form.is_urgent && (
                      <div className="basis-full">
                        <Input
                          value={form.urgent_reason || ""}
                          onChange={(e) => update("urgent_reason", e.target.value)}
                          placeholder="Why is this urgent? (shown on the dashboard tooltip)"
                          className="mt-2"
                          data-testid="field-urgent-reason"
                        />
                      </div>
                    )}
                  </div>
                )}
              </Grid>
            </Section>

            <Section title="Workflow & assignment" icon={Truck}>
              <Grid>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => update("status", v)} disabled={!canEditFields}>
                    <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {allowedStatuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Location">
                  <Input value={form.location || ""} onChange={(e) => update("location", e.target.value)} disabled={!canEditFields || tpDisable("location")} data-testid="field-location" />
                </Field>
                <Field label="Third party">
                  <Select
                    value={form.third_party_id || "none"}
                    onValueChange={(v) => update("third_party_id", v === "none" ? null : v)}
                    disabled={!canEditFields || tpDisable("third_party_id")}
                  >
                    <SelectTrigger data-testid="field-third-party"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {thirdParties.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`${(thirdParties.find((tp) => tp.id === form.third_party_id)?.name) || record.third_party_name || "Third-party"} ref`} mono hint="Their paperwork reference (triple-check when receiving back)">
                  <Input
                    value={form.third_party_po || ""}
                    onChange={(e) => update("third_party_po", e.target.value)}
                    placeholder="e.g. GIA-2026-0042"
                    disabled={!canEditFields || tpDisable("third_party_po")}
                    className="font-mono"
                    data-testid="field-third-party-po"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Notes">
                    <Textarea
                      value={form.notes || ""}
                      onChange={(e) => update("notes", e.target.value)}
                      rows={3}
                      disabled={!canEditFields}
                      data-testid="field-notes"
                    />
                  </Field>
                </div>
              </Grid>
            </Section>

            <Section title="Timeline dates" icon={ClipboardList}>
              <Grid>
                <DateField label="Into WeLove warehouse" value={form.date_received} onChange={(v) => update("date_received", v)} disabled={!canEditFields || tpDisable("date_received")} />
                <DateField label="Ready for collection" value={form.date_ready_for_collection} onChange={(v) => update("date_ready_for_collection", v)} disabled={!canEditFields || tpDisable("date_ready_for_collection")} />
                <DateField label="Collected by Giavani" value={form.date_collected} onChange={(v) => update("date_collected", v)} disabled={!canEditFields} />
                <DateField label="Ready for return (from Giavani)" value={form.date_ready_for_return} onChange={(v) => update("date_ready_for_return", v)} disabled={!canEditFields} testId="field-date-ready-for-return" />
                <DateField label="Expected back to WeLove" value={form.expected_return_date} onChange={(v) => update("expected_return_date", v)} disabled={!canEditFields} />
                <DateField label="Returned" value={form.date_returned} onChange={(v) => update("date_returned", v)} disabled={!canEditFields} />
                <DateField label="Dispatched" value={form.date_dispatched} onChange={(v) => update("date_dispatched", v)} disabled={!canEditFields || tpDisable("date_dispatched")} />
                <DateField label="Delivered" value={form.date_delivered} onChange={(v) => update("date_delivered", v)} disabled={!canEditFields || tpDisable("date_delivered")} />
                <DateField label="Customer required by" value={form.customer_required_date} onChange={(v) => update("customer_required_date", v)} disabled={!canEditFields || tpDisable("customer_required_date")} testId="field-customer-required-date" />
              </Grid>
            </Section>

            <Section title="Dispatch & delivery (UPN)" icon={Package}>
              <Grid>
                <Field label="UPN Consignment number" mono>
                  <Input
                    value={form.upn_consignment_number || ""}
                    onChange={(e) => update("upn_consignment_number", e.target.value)}
                    placeholder="e.g. UPN-1234567"
                    disabled={!canEditFields || tpDisable("upn_consignment_number")}
                    className="font-mono"
                    data-testid="field-upn-consignment"
                  />
                </Field>
                <Field label="Service">
                  <Input
                    value={form.upn_service || "Priority — next working day before 16:30"}
                    onChange={(e) => update("upn_service", e.target.value)}
                    disabled={!canEditFields || tpDisable("upn_service")}
                    data-testid="field-upn-service"
                  />
                </Field>
                <DateField label="Expected delivery date" value={form.expected_delivery_date} onChange={(v) => update("expected_delivery_date", v)} disabled={!canEditFields || tpDisable("expected_delivery_date")} />
              </Grid>
              <div className="mt-3 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3 leading-relaxed">
                <span className="font-semibold text-zinc-700">UPN priority service.</span> Next working day before 16:30. Customer will be called approximately 1 hour before delivery. This info is shown on the customer tracking page once dispatched.
              </div>
            </Section>
          </div>

          {/* Right column */}
          <aside className="space-y-4">
            {/* Spec sheet — compact gallery */}
            <Section title="Spec sheet / images" icon={ImageIcon}>
              <p className="text-[11px] text-zinc-500 mb-2 leading-relaxed">
                Drop the bath photo and Giavani&apos;s PO paperwork here. Customer sees the latest spec on their tracking page.
              </p>
              <SpecImageUploader
                recordId={id}
                images={record.spec_images || []}
                canEdit={canEditFields}
                onChange={refreshAfterImage}
              />
            </Section>

            {/* Documents — PDF attachments (proof of delivery etc.)
                GDPR guard: entire section hidden unless documents.view perm.
                Upload / delete only shown when documents.manage perm. */}
            {hasPerm("documents.view") && (
              <Section
                title={`Documents${record.documents?.length ? ` · ${record.documents.length}` : ""}`}
                icon={FileText}
              >
                <p className="text-[11px] text-zinc-500 mb-2 leading-relaxed">
                  PDF attachments — e.g. the UPN proof of delivery, delivery notes or invoices. Contain customer address / phone — visibility is permission-gated.
                </p>
                <DocumentsPanel
                  recordId={id}
                  documents={record.documents || []}
                  canEdit={canEditFields && hasPerm("documents.manage")}
                  onChange={refreshAfterImage}
                />
              </Section>
            )}

            {/* Comments — Spotify-style conversation, placed under spec images */}
            <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="comments-section">
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2.5">
                <MessageSquare className="h-4 w-4 text-zinc-700" />
                <h3 className="font-heading font-bold text-sm tracking-tight text-zinc-950">
                  Comments
                </h3>
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold tracking-tight ${
                    commentCount > 0 ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-400"
                  }`}
                  data-testid="comments-count-badge"
                  title={`${commentCount} comment${commentCount === 1 ? "" : "s"}`}
                >
                  {commentCount}
                </span>
              </div>
              <div className="p-4">
                <CommentsPanel
                  recordId={id}
                  canComment={!isReadOnly}
                  onCountChange={setCommentCount}
                />
              </div>
            </div>

            {/* Audit trail */}
            <Section title="Audit trail" icon={History}>
              {audit.length === 0 && (
                <p className="text-sm text-zinc-500">No audit entries yet.</p>
              )}
              <ul className="relative space-y-3">
                {audit.map((entry, idx) => (
                  <li key={entry.id} className="relative pl-6">
                    <span className={`absolute left-1 top-1.5 w-2 h-2 rounded-full ${entry.action === "status_change" ? "bg-zinc-950" : "bg-zinc-300"}`} />
                    {idx < audit.length - 1 && (
                      <span className="absolute left-[7px] top-3 bottom-[-12px] w-px bg-zinc-200" />
                    )}
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {entry.action.replace("_", " ")} · {entry.source.replace("_", " ")}
                    </div>
                    <div className="text-sm text-zinc-900 mt-0.5">
                      <span className="font-semibold">{entry.field}</span>
                      {entry.previous_value !== null && entry.new_value !== null && (
                        <>
                          : <span className="line-through text-zinc-400">{entry.previous_value || "—"}</span>
                          {" → "}
                          <span className="text-zinc-900 font-medium">{entry.new_value}</span>
                        </>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {entry.user_name} <span className="text-zinc-400">({entry.company})</span> · {formatDate(entry.timestamp)}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Brightpearl link" icon={Building2}>
              <Spec label="BP ID" value={record.brightpearl_id || "—"} mono />
              <Spec label="Last synced" value={record.brightpearl_last_synced ? formatDate(record.brightpearl_last_synced) : "Never"} mono />
            </Section>
          </aside>
        </div>
      </main>
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-zinc-200 rounded-md shadow-sm">
    <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
      <h3 className="font-heading font-bold text-sm tracking-tight text-zinc-950">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ label, mono, hint, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
    <div className={`mt-1.5 ${mono ? "font-mono" : ""}`}>{children}</div>
    {hint && <p className="text-[10px] text-zinc-400 mt-1">{hint}</p>}
  </div>
);

const DateField = ({ label, value, onChange, disabled, testId }) => (
  <Field label={label}>
    <Input
      type="datetime-local"
      value={value ? new Date(value).toISOString().slice(0, 16) : ""}
      onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
      disabled={disabled}
      data-testid={testId}
    />
  </Field>
);

const Spec = ({ label, value, mono, hint }) => (
  <div className="flex flex-col mb-2 last:mb-0">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
      {hint && <span className="text-zinc-400 normal-case tracking-normal font-normal ml-1">· {hint}</span>}
    </span>
    <span className={`text-xs ${mono ? "font-mono" : ""} text-zinc-900 font-medium`}>{value || "—"}</span>
  </div>
);
